import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { closeChat, updateCs } from '@/store/modules/cs';
import { Icon } from '@successmode-ktp/kui';
import styled, { css } from 'styled-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import InquiryButtons from '@/components/sideNavigation/Cs/home/InquiryButtons';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getChatMessage, readAlert, sendChat } from '@/api/cs';
import { ChatMessage, RefundCreateAccept } from '@/types/cs.type';
import CsChatMessageItem from '@/components/sideNavigation/Cs/chatMessage/CsChatMessageItem';
import { getChatType } from '@/constants/cs';
import { is } from 'date-fns/locale';

function CsChatMessage() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const {
    hasOpenChatAnimation,
    chatType,
    chatId,
    isOpenAlert,
    activeTab,
    isHiddenInquiryButtons,
  } = useAppSelector((state) => state.cs);
  const chatListRef = useRef<HTMLOListElement>(null);
  const [isDisabledInput, setIsDisabledInput] = useState(false);
  const [chatList, setChatList] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const { mutate: mutateReadAlert } = useMutation(readAlert);
  const { data } = useQuery(
    ['ChatMessageList', chatId],
    () => getChatMessage(chatId as number),
    {
      onSuccess: (data) => {
        setChatList(data.chat.messages);
        if (chatId) {
          mutateReadAlert({
            chatId,
            notificationType: 'ONE_ON_ONE',
          });
          if (data.chat.chatType === 'ADMIN_NOTICE') {
            mutateReadAlert({
              chatId,
              notificationType: 'ADMIN_NOTICE',
            });
          }
        }
      },
      enabled: !!chatId,
    },
  );

  const isAdminChat = () => {
    return chatType === 'ADMIN_NOTICE';
  };

  const onCloseChat = () => {
    if (isOpenAlert) {
      queryClient.invalidateQueries('AlertList');
    }
    if (activeTab === 'CHAT') {
      queryClient.invalidateQueries('ChatList');
    }
    if (activeTab === 'RESULT') {
      queryClient.invalidateQueries('ResultList');
    }
    queryClient.invalidateQueries('messageAlertList');
    dispatch(closeChat());
  };

  const { mutate: mutateSendChat } = useMutation(sendChat);

  const onSendChat = () => {
    setIsDisabledInput(true);
    mutateSendChat(
      !!chatId
        ? {
            chatId,
            messageType: 'APPEND_TEXT_REQUEST',
            content: inputValue.trim(),
          }
        : {
            messageType: 'TEXT_REQUEST',
            content: inputValue.trim(),
          },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries('ChatMessageList');
          dispatch(
            updateCs({
              chatId: data.chatId,
            }),
          );
          setIsDisabledInput(false);
        },
        onError: () => {
          setIsDisabledInput(false);
        },
      },
    );

    setInputValue('');
  };

  useEffect(() => {
    const scrollToBottom = () => {
      const container = chatListRef.current;
      const lastChat = container?.lastElementChild;
      const lastChatHeight = lastChat?.clientHeight || 0;
      if (container && lastChatHeight) {
        container.scrollTop = container.scrollHeight;
      }
    };
    if (chatList.length > 0 && !!chatId) {
      scrollToBottom();
    }
  }, [chatList, chatId]);

  const createTime = useMemo(() => {
    if (isAdminChat()) {
      return 'KTP 고객지원팀';
    }

    if (!chatId && chatList.length === 0) {
      // 새로운 채팅
      return `오늘 ${format(new Date(), 'HH:mm')}`;
    }

    if (data?.chat.createdDate && !!chatId && chatList.length > 0) {
      if (
        format(new Date(data.chat.createdDate), 'yyyy-MM-dd') ===
        format(new Date(), 'yyyy-MM-dd')
      ) {
        return format(new Date(data.chat.createdDate), '오늘 HH:mm');
      }

      return format(new Date(data.chat.createdDate), 'yyyy-MM-dd HH:mm');
    }
  }, [chatId, chatList, data]);

  const isHiddenChatMessageTime = (index: number) => {
    return (
      index > 0 &&
      getChatType(chatList[index - 1].type) ===
        getChatType(chatList[index].type) &&
      format(new Date(chatList[index - 1].createdAt), 'yyyy-MM-dd HH:mm') ===
        format(new Date(chatList[index].createdAt), 'yyyy-MM-dd HH:mm')
    );
  };

  const refundInfo = chatList.find(
    (message) => message.type === 'REFUND_CREATE_ACCEPTED',
  );

  const showInquiryButtons = !chatId && !isHiddenInquiryButtons;
  const isNewRefundChat = !chatId && chatType === 'REFUND';
  const isNewPackageChat = !chatId && chatType === 'PACKAGE';

  return (
    <ChatModal>
      <Header>
        <SubTitle>
          <PrevButton onClick={onCloseChat}>
            <Icon.ChevronLeft fillColor='#5F6165' />
          </PrevButton>
          {!isAdminChat() && <ChatTagLabel>대화 시작</ChatTagLabel>}
          <ChatLabel>{createTime}</ChatLabel>
        </SubTitle>
      </Header>
      <ChatMessageList
        id='chat-message-list'
        ref={chatListRef}
        height={
          chatType === 'PACKAGE'
            ? 'calc(100% - 88px)'
            : data?.chat.state === 'CLOSED'
            ? '407px'
            : '438px'
        }
      >
        {isNewRefundChat && <CsChatMessageItem requestType='REFUND' />}
        {isNewPackageChat && <CsChatMessageItem requestType='PACKAGE' />}
        {chatList.length > 0 &&
          chatList.map((chat, index: number) => (
            <CsChatMessageItem
              key={index}
              chat={chat}
              refundInfo={refundInfo as RefundCreateAccept}
              chatMessageType={getChatType(chat.type)}
              isHiddenTime={isHiddenChatMessageTime(index)}
            />
          ))}
      </ChatMessageList>
      {data?.chat.state === 'CLOSED' ? (
        <ClosedFooter>
          <strong>- 종료된 채팅입니다 -</strong>
          <p>{format(new Date(data.chat.deletedAt), 'yyyy-MM-dd HH:mm')}</p>
        </ClosedFooter>
      ) : chatType !== 'PACKAGE' ? (
        <Footer hasAnimation={hasOpenChatAnimation}>
          {showInquiryButtons && <InquiryButtons />}
          <SendWrapper>
            <StyledInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder='문의하실 내용을 입력하세요'
              autoFocus
              onKeyUp={(e) => {
                e.preventDefault();
                if (e.key === 'Enter') {
                  onSendChat();
                }
              }}
              disabled={isDisabledInput}
            />
            <SendButton onClick={onSendChat} disabled={!inputValue}>
              <Icon.Sending fillColor={!!inputValue ? '#3A3B3E' : '#A7A7A7'} />
            </SendButton>
          </SendWrapper>
        </Footer>
      ) : null}
    </ChatModal>
  );
}

const ChatModal = styled.aside`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  width: 100%;
  height: 100%;
  z-index: 700;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 20px 14px 20px;
  height: 68px;
`;

const SubTitle = styled.strong`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
`;

const ChatTagLabel = styled.span`
  color: #5089f8;
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
  background-color: #f4f8ff;
  padding: 1px 6px;
  border-radius: 2px;
  margin-right: 6px;
`;

const ChatLabel = styled.span`
  color: #000;
`;

const PrevButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 0;
  margin-right: 10px;
`;

const ChatMessageList = styled.ol<{ height: string }>`
  height: ${({ height }) => height};
  padding: 0 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div<{ hasAnimation: boolean }>`
  position: absolute;
  bottom: 29px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  padding: 0 14px;
  ${({ hasAnimation }) =>
    hasAnimation
      ? css`
          animation: slide-down 0.3s ease-in-out forwards;
          @keyframes slide-down {
            0% {
              bottom: 86px;
            }
            100% {
              bottom: 29px;
            }
          }
        `
      : css`
          bottom: 29px;
        `}
`;

const SendWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  border-radius: 120px;
  padding: 10px 16px 10px 20px;
  background-color: #f5f6f7;
  border: 1px solid #e5e6e8;
  box-shadow: 0;
  transition: all 300ms ease-in-out;

  &:has(input:focus) {
    box-shadow: 0px 0px 0px 4px #e9f0fe;
    border-color: #246cf6;
    background-color: #fff;
  }
`;

const StyledInput = styled.input`
  width: calc(100% - 24px - 8px); // icon, gap
  border: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  z-index: 100;
  background-color: #f5f6f7;
  transition: all 300ms ease-in-out;
  &::placeholder {
    color: #a7a7a7;
  }

  &:focus {
    background-color: #fff;
  }
`;

const SendButton = styled.button`
  position: absolute;
  top: 50%;
  right: 17px;
  transform: translateY(-50%);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const ClosedFooter = styled.footer`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 123px;
  padding-top: 24px;
  color: #5f6165;
  background-color: #fafbfb;

  strong {
    font-weight: 600;
    font-size: 16px;
    line-height: 150%;
  }

  p {
    font-size: 14px;
    line-height: 150%;
  }
`;

export default CsChatMessage;
